// src/utils/userProfile.ts
import { supabase } from './supabaseClient';

export type UserProfileRow = {
  id: string;
  full_name: string | null;
  gender: string | null;
  dob_day: number | null;
  dob_month: number | null;
  dob_year: number | null;
  tob_hour: number | null;
  tob_minute: number | null;
  tob_second: number | null;
  place_label: string | null;
  place_lat: number | null;
  place_lng: number | null;
  place_timezone: string | null;
  created_at?: string | null;
  updated_at?: string | null;
};

// ===== CACHE CONFIGURATION =====
const CACHE_KEY_PREFIX = 'user_profile_v1_';
const CACHE_EXPIRY_MS = 5 * 60 * 1000; // 5 minutes

// Memory cache to avoid localStorage I/O on every call
const memoryCache = new Map<string, { data: UserProfileRow | null; timestamp: number }>();

export async function fetchMyUserProfile(userId: string) {
  const cacheKey = `${CACHE_KEY_PREFIX}${userId}`;
  const now = Date.now();

  // Check memory cache first (fastest)
  const memoryCached = memoryCache.get(cacheKey);
  if (memoryCached && (now - memoryCached.timestamp < CACHE_EXPIRY_MS)) {
    console.log('[fetchMyUserProfile] Cache hit (memory):', userId);
    return memoryCached.data;
  }

  // Check localStorage cache
  try {
    const storedStr = localStorage.getItem(cacheKey);
    if (storedStr) {
      const stored = JSON.parse(storedStr);
      if (now - stored.timestamp < CACHE_EXPIRY_MS) {
        console.log('[fetchMyUserProfile] Cache hit (localStorage):', userId);
        // Update memory cache
        memoryCache.set(cacheKey, { data: stored.data, timestamp: stored.timestamp });
        return stored.data as UserProfileRow | null;
      }
      // Expired - remove from localStorage
      localStorage.removeItem(cacheKey);
    }
  } catch (e) {
    console.warn('[fetchMyUserProfile] Cache read error:', e);
  }

  // Cache miss - fetch from database
  const { data, error } = await supabase
    .from('users')
    .select('*')
    .eq('id', userId)
    .maybeSingle();

  if (error) throw error;

  // Store in both caches
  const cacheEntry = { data, timestamp: now };
  memoryCache.set(cacheKey, cacheEntry);
  try {
    localStorage.setItem(cacheKey, JSON.stringify(cacheEntry));
  } catch (e) {
    console.warn('[fetchMyUserProfile] Cache write error:', e);
  }

  return data as UserProfileRow | null;
}

/**
 * Invalidate cache when profile is updated
 * Call this after upsertMyUserProfile() or any profile mutation
 */
export function invalidateProfileCache(userId: string) {
  const cacheKey = `${CACHE_KEY_PREFIX}${userId}`;
  memoryCache.delete(cacheKey);
  try {
    localStorage.removeItem(cacheKey);
  } catch (e) {
    console.warn('[invalidateProfileCache] Error:', e);
  }
  console.log('[invalidateProfileCache] Cache cleared for:', userId);
}

// (Optional) If you ever need to programmatically save again:
export async function upsertMyUserProfile(payload: Partial<UserProfileRow> & { id: string }) {
  const { data, error } = await supabase
    .from('users')
    .upsert(payload, { onConflict: 'id' })
    .select()
    .maybeSingle();

  if (error) throw error;
  
  // Invalidate cache after update
  invalidateProfileCache(payload.id);
  
  return data as UserProfileRow | null;
}
